import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DashLayout from '../../components/DashLayout';
import { useAuth } from '../../context/AuthContext';

const nav = [
  { to: '/dashboard',              icon: '📊', label: 'Dashboard' },
  { to: '/dashboard/chats',        icon: '💬', label: 'Live Chats' },
  { to: '/dashboard/leads',        icon: '👥', label: 'Leads' },
  { to: '/dashboard/crm',          icon: '📋', label: 'CRM' },
  { to: '/dashboard/agents',       icon: '👤', label: 'Agents' },
  { to: '/dashboard/tickets',      icon: '🎫', label: 'Support Tickets' },
  { to: '/dashboard/ai-bot',       icon: '🤖', label: 'AI Chat Bot' },
  { to: '/dashboard/api',          icon: '🔑', label: 'API Access' },
  { to: '/dashboard/branding',     icon: '🎨', label: 'Branding' },
  { to: '/dashboard/widget',       icon: '🔌', label: 'Widget & Script' },
  { to: '/dashboard/contact-form', icon: '📝', label: 'Contact Form' },
  { to: '/dashboard/analytics',    icon: '📈', label: 'Analytics' },
  { to: '/dashboard/billing',      icon: '💳', label: 'Billing' },
  { to: '/dashboard/settings',     icon: '⚙️',  label: 'Settings' },
];

const StatCard = ({ label, value, sub, color = '#00d55b' }) => (
  <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 20 }}>
    <div style={{ fontSize: 12, color: '#555', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: '#555', marginTop: 6 }}>{sub}</div>}
  </div>
);

const NOTIF_ICONS = {
  new_lead:         { icon: '👥', color: '#3b82f6', label: 'New Lead' },
  new_chat:         { icon: '💬', color: '#00d55b', label: 'New Chat' },
  payment_approved: { icon: '✅', color: '#00d55b', label: 'Payment Approved' },
  payment_rejected: { icon: '❌', color: '#ff4444', label: 'Payment Rejected' },
  payment_reminder: { icon: '💳', color: '#f59e0b', label: 'Payment Reminder' },
  plan_expiring:    { icon: '⏰', color: '#f59e0b', label: 'Plan Expiring Soon' },
  plan_expired:     { icon: '🔴', color: '#ff4444', label: 'Plan Expired' },
};

export default function BusinessDashboard() {
  const { user } = useAuth();
  const [analytics,    setAnalytics]    = useState(null);
  const [agents,       setAgents]       = useState([]);
  const [recentChats,  setRecentChats]  = useState([]);
  const [profile,      setProfile]      = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount,  setUnreadCount]  = useState(0);
  const [showNotifs,   setShowNotifs]   = useState(false);
  const [notifsLoading, setNotifsLoading] = useState(false);

  const loadData = useCallback(() => {
    axios.get('/api/chats/analytics').then(r => setAnalytics(r.data.data)).catch(() => {});
    axios.get('/api/agents').then(r => setAgents(r.data.data || [])).catch(() => {});
    axios.get('/api/chats?limit=5').then(r => setRecentChats(r.data.data || [])).catch(() => {});
    axios.get('/api/business/profile').then(r => setProfile(r.data.data)).catch(() => {});
  }, []);

  const loadNotifications = useCallback(() => {
    setNotifsLoading(true);
    axios.get('/api/business/notifications').then(r => {
      const notifs = r.data.data || [];
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.is_read).length);
    }).catch(() => {}).finally(() => setNotifsLoading(false));
  }, []);

  useEffect(() => {
    loadData();
    loadNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [loadData, loadNotifications]);

  const markAllRead = async () => {
    try {
      await axios.put('/api/business/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const markRead = async (id) => {
    try {
      await axios.put(`/api/business/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const setupItems = [
    { label: 'Set up branding',        done: !!(profile?.logo_url || profile?.company_display_name), to: '/dashboard/branding', icon: '🎨' },
    { label: 'Add agents',             done: agents.length > 0,                                       to: '/dashboard/agents',  icon: '👥' },
    { label: 'Configure widget',       done: !!(profile?.welcome_message),                            to: '/dashboard/widget',  icon: '⚙️' },
    { label: 'Embed script on website',done: !!(user?.bot_id),                                        to: '/dashboard/widget',  icon: '🔌' },
  ];
  const setupDone = setupItems.filter(s => s.done).length;

  // Use profile for accurate expiry (profile has plan_expires_at)
  const expiryDate = profile?.plan_expires_at ? new Date(profile.plan_expires_at) : null;
  const daysLeft   = expiryDate ? Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24)) : null;

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1)   return 'Just now';
    if (mins < 60)  return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)   return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <DashLayout navItems={nav} role="Business Admin">
      <div style={{ padding: 28 }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700 }}>Welcome back, {user?.company_name}!</h1>
            <p style={{ color: '#555', fontSize: 13, marginTop: 4, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ background: 'rgba(0,213,91,0.1)', color: '#00d55b', padding: '2px 10px', borderRadius: 20, fontSize: 12 }}>
                {profile?.plan_name || user?.plan_key?.replace(/_/g, ' ')} · Active
              </span>
              {daysLeft !== null && (
                <span style={{ color: daysLeft <= 7 ? '#ff4444' : daysLeft <= 14 ? '#f59e0b' : '#555', fontSize: 12 }}>
                  {daysLeft > 0 ? `⏱ ${daysLeft} days remaining` : '🔴 Plan expired'}
                </span>
              )}
            </p>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>

            {/* 🔔 Notification Bell */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => { setShowNotifs(v => !v); if (!showNotifs) markAllRead(); }}
                style={{ position: 'relative', width: 40, height: 40, borderRadius: 10, background: '#111', border: '1px solid #1e1e1e', cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                🔔
                {unreadCount > 0 && (
                  <div style={{ position: 'absolute', top: -5, right: -5, minWidth: 18, height: 18, background: '#ff4444', borderRadius: 9, fontSize: 10, color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </div>
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotifs && (
                <div style={{ position: 'absolute', top: 48, right: 0, width: 360, background: '#111', border: '1px solid #1e1e1e', borderRadius: 14, zIndex: 1000, boxShadow: '0 8px 40px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
                  <div style={{ padding: '14px 16px', borderBottom: '1px solid #1e1e1e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>🔔 Notifications</span>
                    {notifications.some(n => !n.is_read) && (
                      <button onClick={markAllRead} style={{ background: 'none', border: 'none', color: '#00d55b', fontSize: 12, cursor: 'pointer' }}>Mark all read</button>
                    )}
                  </div>
                  <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    {notifsLoading ? (
                      <div style={{ padding: 24, textAlign: 'center', color: '#555', fontSize: 13 }}>Loading...</div>
                    ) : notifications.length === 0 ? (
                      <div style={{ padding: 32, textAlign: 'center' }}>
                        <div style={{ fontSize: 32, marginBottom: 8 }}>🔕</div>
                        <div style={{ color: '#555', fontSize: 13 }}>No notifications yet</div>
                      </div>
                    ) : notifications.map(n => {
                      const meta = NOTIF_ICONS[n.type] || { icon: '📢', color: '#555', label: 'Notification' };
                      return (
                        <div key={n.id} onClick={() => markRead(n.id)}
                          style={{ padding: '12px 16px', borderBottom: '1px solid #0d0d0d', background: n.is_read ? 'transparent' : 'rgba(0,213,91,0.03)', display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer' }}>
                          <div style={{ width: 36, height: 36, borderRadius: 10, background: `${meta.color}15`, border: `1px solid ${meta.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                            {meta.icon}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: meta.color }}>{meta.label}</span>
                              <span style={{ fontSize: 11, color: '#333' }}>{timeAgo(n.created_at)}</span>
                            </div>
                            <div style={{ fontSize: 13, color: '#fff', marginBottom: 2 }}>{n.title}</div>
                            <div style={{ fontSize: 12, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.message}</div>
                          </div>
                          {!n.is_read && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#00d55b', flexShrink: 0, marginTop: 4 }} />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <Link to="/dashboard/chats" style={{ padding: '10px 20px', background: '#00d55b', color: '#000', borderRadius: 8, fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              💬 Live Chats {analytics?.today_chats > 0 && `(${analytics.today_chats})`}
            </Link>
          </div>
        </div>

        {/* ── Plan Expiry Warning ── */}
        {daysLeft !== null && daysLeft <= 14 && daysLeft > 0 && (
          <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 12, padding: '14px 18px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 13, color: '#f59e0b' }}>
              ⚠️ Your plan expires in <strong>{daysLeft} days</strong> on <strong>{expiryDate?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>. Renew now to avoid interruption.
            </div>
            <Link to="/dashboard/billing" style={{ padding: '7px 16px', background: '#f59e0b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0 }}>
              🔄 Renew Now
            </Link>
          </div>
        )}
        {daysLeft !== null && daysLeft <= 0 && (
          <div style={{ background: 'rgba(255,68,68,0.08)', border: '1px solid rgba(255,68,68,0.25)', borderRadius: 12, padding: '14px 18px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 13, color: '#ff4444' }}>
              🔴 Your plan has <strong>expired</strong>. Renew now to restore access.
            </div>
            <Link to="/dashboard/billing" style={{ padding: '7px 16px', background: '#ff4444', color: '#fff', borderRadius: 8, fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0 }}>
              🔄 Renew Now
            </Link>
          </div>
        )}

        {/* ── Setup checklist ── */}
        {setupDone < setupItems.length && (
          <div style={{ background: '#0a1a0d', border: '1px solid rgba(0,213,91,0.2)', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: '#00d55b' }}>🚀 Setup Checklist ({setupDone}/{setupItems.length})</h3>
              <div style={{ background: '#001a0d', borderRadius: 20, padding: '3px 10px', fontSize: 12, color: '#00d55b' }}>{Math.round((setupDone / setupItems.length) * 100)}% complete</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
              {setupItems.map(item => (
                <Link key={item.to} to={item.to} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: item.done ? 'rgba(0,213,91,0.08)' : '#111', border: `1px solid ${item.done ? 'rgba(0,213,91,0.2)' : '#1e1e1e'}`, borderRadius: 8, textDecoration: 'none' }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: item.done ? '#00d55b' : '#1e1e1e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: item.done ? '#000' : '#555', flexShrink: 0 }}>{item.done ? '✓' : '○'}</div>
                  <span style={{ fontSize: 13, color: item.done ? '#00d55b' : '#888' }}>{item.label}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* ── Stats ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
          <StatCard label="Total chats"  value={analytics?.total_chats ?? '—'}  sub="All time" />
          <StatCard label="Today's chats" value={analytics?.today_chats ?? '—'} sub="Last 24 hours" />
          <StatCard label="Resolved"     value={analytics?.resolved_chats ?? '—'} sub="Successfully closed" color="#fff" />
          <StatCard label="Missed"       value={analytics?.missed_chats ?? '—'}  sub="No agent responded" color={analytics?.missed_chats > 0 ? '#ff4444' : '#555'} />
          <StatCard label="Avg rating"   value={analytics?.avg_rating ? `${analytics.avg_rating}★` : '—'} sub="Visitor feedback" color="#f59e0b" />
          <StatCard label="Chats used"   value={`${analytics?.chats_used_this_month ?? 0}/${analytics?.max_chats_per_month ?? '∞'}`} sub="This month" color="#fff" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20 }}>
          {/* ── Recent chats ── */}
          <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #1e1e1e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: 14, fontWeight: 600 }}>Recent Chats</h3>
              <Link to="/dashboard/chats" style={{ fontSize: 12, color: '#00d55b' }}>View all →</Link>
            </div>
            {recentChats.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#333', fontSize: 13 }}>No chats yet. Embed the widget to get started!</div>
            ) : recentChats.map(chat => (
              <div key={chat.id} style={{ padding: '12px 20px', borderBottom: '1px solid #0d0d0d', display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: '#00d55b', flexShrink: 0 }}>{chat.visitor_name?.[0]?.toUpperCase() || '?'}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{chat.visitor_name || 'Unknown'}</div>
                  <div style={{ fontSize: 12, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chat.last_message || 'No messages'}</div>
                </div>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: chat.status === 'resolved' ? 'rgba(0,213,91,0.1)' : chat.status === 'open' ? 'rgba(245,158,11,0.1)' : '#1a1a1a', color: chat.status === 'resolved' ? '#00d55b' : chat.status === 'open' ? '#f59e0b' : '#555' }}>{chat.status}</span>
              </div>
            ))}
          </div>

          {/* ── Agents ── */}
          <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #1e1e1e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: 14, fontWeight: 600 }}>Agents</h3>
              <Link to="/dashboard/agents" style={{ fontSize: 12, color: '#00d55b' }}>Manage →</Link>
            </div>
            {agents.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center' }}>
                <div style={{ color: '#333', fontSize: 13, marginBottom: 12 }}>No agents yet</div>
                <Link to="/dashboard/agents" style={{ fontSize: 13, color: '#00d55b' }}>+ Add Agent</Link>
              </div>
            ) : agents.map(agent => (
              <div key={agent.id} style={{ padding: '10px 16px', borderBottom: '1px solid #0d0d0d', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: agent.online_status === 'online' ? '#00d55b' : '#333', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{agent.name}</div>
                  <div style={{ fontSize: 11, color: '#555' }}>{agent.role === 'main_agent' ? 'Main Agent' : 'Agent'}</div>
                </div>
                <div style={{ fontSize: 11, color: agent.online_status === 'online' ? '#00d55b' : '#555' }}>{agent.online_status}</div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </DashLayout>
  );
}